import { offset, shift } from "@floating-ui/react";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import { inject } from "../ng";
import { Popover, PopoverButton, PopoverPanel } from "../popover/popover";
import { withSafeUrl } from "../url";
import classes from "./header.module.css";
import { Label } from "./label";

const mainAxis = 6;
const crossAxis = -mainAxis;
const middleware = [offset({ mainAxis, crossAxis }), shift()];

const panelStyle = {
	maxWidth: `min(300px, calc(100svw - 2 * ${mainAxis}px))`,
	maxHeight: `calc(100svh - var(--header-height) - var(--footer-height) - 2 * ${mainAxis}px)`,
};

export function Menu() {
	const { apps } = useApps();
	return (
		<Popover placement="bottom-end" strategy="fixed" middleware={middleware}>
			<PopoverButton className={classes.item}>
				<Label icon="/img/webtools-icon.svg">Menu</Label>
			</PopoverButton>
			<PopoverPanel style={panelStyle}>
				<div className={classes.appType}>Licensed Apps</div>
				{apps.licensed.map((app) => (
					<App key={app.id} {...app} />
				))}
				<div className={classes.appType}>Unlicensed Apps</div>
				{apps.unlicensed.map((app) => (
					<App key={app.id} {...app} />
				))}
				<a className={classes.logout} href="/logout">
					Logout
				</a>
			</PopoverPanel>
		</Popover>
	);
}

export function App(props: App) {
	const { name, homeUrl } = props;
	const [rewriteUrl, setRewriteUrl] = useState(props.homeUrl);
	const id = useId();

	/**
	 * The `focus` event is used because the `click` event does not capture middle mouse click nor context menu "Open link in new tab/window".
	 * The `contextmenu` event only activates when the browser context menu opens, but it doesn't track which item the user presses; they could press "Inspect" for example which doesn't need a URL rewrite.
	 * The `auxclick` event tracks middle mouse click, but it also tracks when the context menu opens (which is not applicable, as explained above).
	 */
	const addCenterToQuery = useMemo(() => {
		switch (name) {
			case "ContentTools":
			case "MapExact":
			case "MapSure":
			case "PC*MILER Web":
				return () => {
					const url = new URL(homeUrl);
					const params = new URLSearchParams(location.search);
					const param = "center";
					const center = params.get(param);
					if (center) {
						url.searchParams.set(param, center);
					}
					setRewriteUrl(url.href);
				};
		}
	}, [name, homeUrl]);

	return (
		<a
			className={classes.app}
			href={rewriteUrl || homeUrl}
			onFocus={addCenterToQuery}
			target="_blank"
			rel="noopener noreferrer"
		>
			<img
				className={classes.appIcon}
				aria-labelledby={id}
				src={`/img/${props.imageSource}`}
			/>
			<span className={classes.appName} id={id}>
				{name}
			</span>
			<p className={classes.appDesc}>{props.description}</p>
		</a>
	);
}

export type App = {
	licensed: boolean;
	expired: boolean;
	id: number;
	name: string;
	description: string;
	homeUrl: string;
	imageSource: string;
	currentTermsOfUse: number;
};

export function useApps() {
	const query = useQuery(appsQuery);

	const { data } = query;

	const apps = useMemo(() => {
		const licensed: App[] = [];
		const unlicensed: App[] = [];

		if (data) {
			for (const app of data) {
				const ALK = "ALK ";
				if (app.name.startsWith(ALK)) {
					app.name = app.name.substring(ALK.length);
				}

				if (app.licensed) {
					licensed.push(app);
				} else {
					unlicensed.push(app);
				}
			}
		}

		return { licensed, unlicensed };
	}, [data]);

	return { apps, query };
}

export const appsQuery = {
	queryKey: ["apps"],
	async queryFn({ signal }) {
		const token = inject("UserProvider").getUser().authToken;
		const url = withSafeUrl(
			window.$config.ssoUrl,
		)`./v1/users/${token}/products`;
		return await (
			await fetch(url, {
				method: "GET",
				headers: { accept: "application/json" },
				signal,
			})
		).json();
	},
} satisfies UseQueryOptions<App[], unknown, App[], string[]>;
